
import { useState, type FormEvent } from 'react';

const FORM_URL = "https://readdy.ai/api/form/d6cheb3v53dtlm7j854g";

export default function ConsultationCTA() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    // Validate textarea length
    const message = formData.get('message') as string;
    if (message && message.length > 500) {
      return;
    }

    setIsSubmitting(true);
    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        params.append(key, value.toString());
      });

      const response = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      if (!response.ok) {
        throw new Error(`Network response was not ok (${response.status})`);
      }

      setIsSubmitted(true);
      form.reset();
    } catch (error) {
      console.error('Form submission failed:', error);
      // Optionally display a user‑friendly error message here
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="consultation" className="py-24 bg-gradient-to-br from-brand-navy to-brand-navy-dark relative overflow-hidden">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-0 w-96 h-96 bg-brand-red rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-brand-gold rounded-full blur-3xl translate-x-1/2 translate-y-1/2"></div>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 relative z-10">
        <div className="flex flex-col lg:flex-row gap-16 items-center">
          {/* Left content */}
          <div className="flex-1 text-center lg:text-left">
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
              Ready To Lower Your Energy Bills?
            </h2>
            <p className="text-xl text-white/90 mb-8 leading-relaxed">
              Get a free, no-obligation home energy assessment. Our experts will design a custom solution that maximizes your savings and comfort.
            </p>

            <div className="grid grid-cols-2 gap-4 mb-8">
              {[
                { icon: "ri-shield-check-line", text: "Licensed & Insured" },
                { icon: "ri-money-dollar-circle-line", text: "$0 Down Financing" },
                { icon: "ri-star-line", text: "4.9 Star Rating" },
                { icon: "ri-time-line", text: "Same-Day Response" },
              ].map((item) => (
                <div key={item.text} className="flex items-center gap-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-white/10 rounded-lg flex-shrink-0">
                    <i className={`${item.icon} text-brand-gold text-lg`}></i>
                  </div>
                  <span className="text-white/90 text-sm font-medium">{item.text}</span>
                </div>
              ))}
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
              <p className="text-white font-semibold mb-2">Call Us Directly</p>
              <a href="tel:+18005551234" className="text-3xl font-bold text-white hover:text-brand-gold transition-colors cursor-pointer">
                (800) 555-1234
              </a>
              <p className="text-white/70 text-sm mt-2">Available Monday - Saturday, 8am - 6pm</p>
            </div>
          </div>

          {/* Right form */}
          <div className="flex-1 w-full max-w-lg">
            {isSubmitted ? (
              <div className="bg-white rounded-3xl p-10 text-center shadow-2xl">
                <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-6">
                  <i className="ri-check-line text-green-600 text-3xl"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Thank You!</h3>
                <p className="text-gray-600">
                  We&apos;ve received your request. A member of our team will contact you within 24 hours to schedule your free assessment.
                </p>
              </div>
            ) : (
              <form
                id="service-consultation-form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="bg-white rounded-3xl p-8 shadow-2xl"
              >
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Get Your Free Assessment</h3>
                <p className="text-gray-500 text-sm mb-6">
                  Fill out the form and we&apos;ll get back to you within 24 hours.
                </p>

                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                      <input
                        type="text"
                        name="first_name"
                        required
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-brand-red focus:ring-1 focus:ring-brand-red"
                        placeholder="John"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                      <input
                        type="text"
                        name="last_name"
                        required
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-brand-red focus:ring-1 focus:ring-brand-red"
                        placeholder="Smith"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <input
                      type="email"
                      name="email"
                      required
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-brand-red focus:ring-1 focus:ring-brand-red"
                      placeholder="john@example.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                    <input
                      type="tel"
                      name="phone"
                      required
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-brand-red focus:ring-1 focus:ring-brand-red"
                      placeholder="(555) 123-4567"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Service Interest</label>
                    <select
                      name="service_interest"
                      required
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-brand-red focus:ring-1 focus:ring-brand-red bg-white cursor-pointer"
                    >
                      <option value="">Select a service</option>
                      <option value="Solar Installation">Solar Installation</option>
                      <option value="HVAC Systems">HVAC Systems</option>
                      <option value="Attic Insulation">Attic Insulation</option>
                      <option value="Solar Attic Fans">Solar Attic Fans</option>
                      <option value="Multiple Services">Multiple Services</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Additional Details (Optional)</label>
                    <textarea
                      name="message"
                      rows={3}
                      maxLength={500}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-brand-red focus:ring-1 focus:ring-brand-red resize-none"
                      placeholder="Tell us about your home and energy goals..."
                    ></textarea>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-brand-red text-white py-4 rounded-xl font-semibold hover:bg-brand-red-dark transition-all duration-300 cursor-pointer whitespace-nowrap disabled:opacity-60"
                  >
                    {isSubmitting ? 'Submitting...' : 'Request Free Assessment'}
                  </button>

                  <p className="text-xs text-gray-400 text-center">
                    No obligation. No pressure. Just honest energy advice.
                  </p>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
